import type { Dispatch, SetStateAction } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import Button from "../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import Dialog from "../../../design-system/components/Dialog/Dialog.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";

/**
 * Confirmation dialog for pausing / resuming the class's screens.
 *
 * One dialog serves both states: it shows a confirm-to-start prompt when no
 * pause is live and a "Fortsetzen" (resume) prompt once one is. Starting a pause
 * deliberately closes the dialog rather than flipping it to the resume prompt —
 * the teacher re-opens it from the menu when they want to resume.
 *
 * Acts via the `manageWorkshop` mutation; the new live state comes back over SSE.
 */
export const PauseDialog = ({
  workshopId,
  paused,
  open,
  setOpen,
}: {
  workshopId: WorkshopId;
  paused: boolean;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const [manageWorkshop] = useMutation(MUTATIONS.manageWorkshop);

  const startPause = () => {
    setOpen(false);
    void manageWorkshop({ workshopId, action: { action: "startPause" } });
  };

  const endPause = () => {
    setOpen(false);
    void manageWorkshop({ workshopId, action: { action: "endPause" } });
  };

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title={paused ? "Bildschirme pausiert" : "Bildschirme pausieren"}
      buttons={[
        paused ? (
          <Button key="pause" variant={Variants.SUCCESS} onClick={endPause}>
            Fortsetzen
          </Button>
        ) : (
          <Button key="pause" variant={Variants.PRIMARY} onClick={startPause}>
            Pausieren
          </Button>
        ),
      ]}
    >
      <Text variant="body" color="neutral-100">
        {paused
          ? "Die Bildschirme deiner Schüler*innen sind aktuell pausiert."
          : "Pausiert alle Schüler*innen Bildschirme. Nur du kannst es wieder aufheben."}
      </Text>
    </Dialog>
  );
};
